import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabase';

/**
 * Add your deployed reset-password URL to Supabase → Authentication → Redirect URLs.
 */
export default function ResetPasswordPage() {
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState('');
    const [status, setStatus] = useState<'ready' | 'saving' | 'done'>('ready');
    const navigate = useNavigate();

    useEffect(() => {
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
            if (event === 'PASSWORD_RECOVERY') {
                setError('');
            }
        });
        return () => subscription.unsubscribe();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }

        setStatus('saving');
        const { error: updateError } = await supabase.auth.updateUser({ password });
        if (updateError) {
            const msg =
                updateError.message?.toLowerCase().includes('session') || updateError.message?.toLowerCase().includes('jwt')
                    ? 'Open this page from the link in your password reset email, then try again.'
                    : updateError.message;
            setError(msg);
            setStatus('ready');
            return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        const uid = user?.id ?? '';
        const [{ data: teacher }, { data: student }] = await Promise.all([
            supabase.from('teachers').select('id').eq('user_id', uid).maybeSingle(),
            supabase.from('students').select('id').eq('user_id', uid).maybeSingle(),
        ]);

        setStatus('done');
        window.setTimeout(() => {
            if (teacher) navigate('/login', { replace: true });
            else if (student) navigate('/student-login', { replace: true });
            else navigate('/login', { replace: true });
        }, 1200);
    };

    if (status === 'done') {
        return (
            <div className="auth-page-shell">
                <div className="page-panel auth-card">
                    <div className="auth-header">
                        <div className="auth-icon-badge" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>OK</div>
                        <p className="hero-eyebrow">Success</p>
                        <h1 className="page-title">Password updated</h1>
                        <p className="auth-subcopy">Redirecting you to sign in…</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page-shell">
            <div className="page-panel auth-card">
                <div className="auth-header">
                    <div className="auth-icon-badge" style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}>PW</div>
                    <p className="hero-eyebrow">Account Recovery</p>
                    <h1 className="page-title">Set a new password</h1>
                    <p className="auth-subcopy">Open this page from your reset email, then choose a secure new password.</p>
                </div>

                <form className="auth-form" onSubmit={handleSubmit}>
                    {error && (
                        <div className="auth-feedback error">
                            {error}
                        </div>
                    )}
                    <input
                        type="password"
                        id="reset-password-new"
                        name="newPassword"
                        className="input-field"
                        placeholder="New password (min 8 characters)"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={8}
                        autoComplete="new-password"
                    />
                    <input
                        type="password"
                        id="reset-password-confirm"
                        name="confirmPassword"
                        className="input-field"
                        placeholder="Confirm new password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        required
                        minLength={8}
                        autoComplete="new-password"
                    />
                    <div className="form-actions">
                        <button type="submit" className="btn primary" disabled={status === 'saving'}>
                            {status === 'saving' ? 'Saving…' : 'Update password'}
                        </button>
                        <div className="auth-links-stack">
                            <Link to="/login" className="btn tertiary">
                                Back to teacher login
                            </Link>
                            <Link to="/student-login" className="btn tertiary">
                                Back to student login
                            </Link>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
